import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	ShiftInfo,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid2 as Grid,
	Paper,
	Typography,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	LazyBackDropLoading,
	LazyLoading,
} from "../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { EAFProcessesTable } from "./components/EAFProcessesTable";
import { useEAFProcessesContext } from "./context/EAFProcessesContext";
import { getProcessesByShift } from "./repositories/EAFRepository";

export const EAFProcessesPage = () => {
	const navigate = useNavigate();
	const { state, actions } = useEAFProcessesContext();
	const [showDeletedProcesses, setShowDeletedProcesses] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [firstRender, setFirstRender] = useState(true);

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const { shiftId: shiftIdParam } = useParams();

	const getDataFromAPI = async (shift?: ShiftInfo) => {
		actions.setIsLoadingGrid(true);
		const shiftId = get(shift ? shift : shiftInfo, "CurrentShiftId", null);
		const resp = await getProcessesByShift(
			Number(shiftId),
			showDeletedProcesses,
		);
		if (resp.ok && resp.data.tables.length > 0) {
			actions.setProcesses(resp.data.tables[0].rows);
		} else {
			actions.setErrorMessage("Error fetching processes data");
		}
		actions.setIsLoadingGrid(false);
	};

	useEffect(() => {
		if (shiftInfo && !firstRender) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			const newSearchParams = new URLSearchParams(searchParams.toString());

			navigate(`/eaf-processes/${shiftId}?${newSearchParams.toString()}`);

			(async () => {
				await getDataFromAPI();
			})();
		}
	}, [shiftInfo, showDeletedProcesses]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				getShiftDataFromAPI(null, shiftId).then(async (shift) => {
					if (shift) {
						await getDataFromAPI(shift);
						setFirstRender(false);
					}
				});
			})();
		} else {
			(async () => {
				getShiftDataFromAPI(null, 0).then(async (shift) => {
					if (shift) {
						await getDataFromAPI(shift);
						setFirstRender(false);
					}
				});
			})();
		}
	}, []);

	if (!state.isLoadingPage) {
		return (
			<>
				<HelmetDexteel title={`EAF Processes`} />
				<Paper
					elevation={0}
					style={{
						width: "100%",
						padding: "15px 20px 10px",
						height: "93vh",
						backgroundColor: ATHENS_GRAY,
					}}
				>
					<LazyBackDropLoading open={state.isLoadingBackground} />
					<Grid container justifyContent="center" alignItems="center">
						<Grid size={{ xs: 12, md: 12 }} style={{ margin: 0 }}>
							<Grid size={{ xs: 12, md: 12 }}>
								<Typography variant="h5" style={{ fontWeight: 600 }}>
									EAF Processes
								</Typography>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }} style={{ marginBottom: "10px" }}>
								<Grid
									container
									direction="row"
									justifyContent="flex-start"
									alignItems="stretch"
									spacing={1}
								>
									<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
										<Paper
											elevation={1}
											style={{
												display: "flex",
												alignItems: "center",
											}}
										>
											<ShiftDayNavigatorControl preventInit withPaper={false} />
										</Paper>
									</Grid>
									<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
										<Paper
											style={{
												minWidth: "300px",
												display: "flex",
												alignItems: "center",
											}}
										>
											<Box sx={{ p: 2 }}>
												<Grid
													container
													spacing={1}
													justifyContent="flex-start"
													alignItems="center"
												>
													<Grid size={{ xs: 12 }}>
														<FormGroup>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={showDeletedProcesses}
																		color="primary"
																		onChange={(event) =>
																			setShowDeletedProcesses(
																				event.target.checked,
																			)
																		}
																	/>
																}
																label="Show deleted processes"
															/>
														</FormGroup>
													</Grid>
												</Grid>
											</Box>
										</Paper>
									</Grid>
								</Grid>
							</Grid>
							<EAFProcessesTable
								loadingGrid={state.isLoadingGrid}
								rows={state.processes || []}
								refreshData={getDataFromAPI}
							/>
						</Grid>
					</Grid>
					<ErrorModal
						error={state.errorMessage}
						onHide={() => actions.setErrorMessage("")}
					/>
					<NotificationSnackBar
						message={state.notificationMessage}
						onHide={() => actions.setNotificationMessage("")}
					/>
				</Paper>
			</>
		);
	}
	return <LazyLoading />;
};
